import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';
import { IonIcon } from '@ionic/react';
import {
  extensionPuzzleOutline,
  cardOutline,
  mailOutline,
  gridOutline,
  ticketOutline,
  fileTrayFullOutline,
  playOutline,
  settingsOutline,
  arrowBackOutline,
  trendingUpOutline,
  keyOutline,
  gitNetworkOutline,
  cubeOutline
} from 'ionicons/icons';
import { menuController } from '@ionic/core/components';
import { useSidebarItems } from '../../hooks/useSidebarItems';
import { isPinnedFor } from '../../hooks/usePinnedItems';
import { appPinId, agentPinId } from '../../utils/pinIds';
import { Apps } from '../../services/apps';
import LetterAvatar from '../atoms/LetterAvatar/LetterAvatar';
import '../../layouts/Sidebar.css'; // Import sidebar styles

// A pinned app/agent rendered as a sidebar nav-link. Shows the item's logo
// (falling back to a generated LetterAvatar on a missing/broken image, same
// as the list pages) + a truncated label, and navigates to its detail page.
const PinnedNavLink = ({ to, iconUrl, label, active, collapsed, onClick }) => {
  const [failed, setFailed] = useState(false);
  return (
    <li className="nav-item">
      <Link
        to={to}
        className={`nav-link nav-link--pinned ${active ? 'active' : ''}`}
        onClick={onClick}
        title={label}
      >
        <span className="nav-icon">
          {iconUrl && !failed ? (
            <img
              src={iconUrl}
              alt=""
              className="nav-pinned-icon"
              loading="lazy"
              onError={() => setFailed(true)}
            />
          ) : (
            <LetterAvatar name={label} className="nav-pinned-icon" />
          )}
        </span>
        {!collapsed && (
          <div className="nav-content">
            <span className="nav-label nav-label--pinned">{label}</span>
          </div>
        )}
      </Link>
    </li>
  );
};

// Custom "four circles" (2x2 dots) icon — ionicons only ships 9-dot (apps) or
// 4-square (grid), and the user wants four round dots. Rendered inline so it
// inherits currentColor + sizing like the ion-icons next to it.
const FourDotsIcon = (
  <svg viewBox="0 0 512 512" width="20" height="20" fill="none"
       stroke="currentColor" strokeWidth="34" aria-hidden="true">
    <circle cx="168" cy="168" r="54" />
    <circle cx="344" cy="168" r="54" />
    <circle cx="168" cy="344" r="54" />
    <circle cx="344" cy="344" r="54" />
  </svg>
);

const NavigationMenu = ({ className = '', isMobile = false, onNavClick, collapsed = false }) => {
  const { t } = useTranslation();
  const location = useLocation();
  // Current project — drives the PRIMARY nav links (Agents/Apps go to THIS
  // project's lists). The pinned list below is account-wide and ignores it.
  const { projectId } = useParams();

  // Pinned apps/agents in the sidebar — ACCOUNT-WIDE across every project the
  // user can access (owned + invited), in ONE request. So the list no longer
  // resets when you switch projects; each entry links to its OWN project.
  // Pin state is still per-project (default pinned), checked via isPinnedFor.
  const { data: sidebar = { agents: [], apps: [] } } = useSidebarItems();
  const apps = sidebar.apps;
  const agents = sidebar.agents;

  // App rows omit the catalog icon, so map appType → icon once (same source
  // AppsPage uses). Non-fatal — a miss just falls back to a LetterAvatar.
  const [appIconByType, setAppIconByType] = useState({});
  useEffect(() => {
    let alive = true;
    Apps.listCatalog()
      .then((data) => {
        if (!alive) return;
        const map = {};
        for (const c of (data?.items || [])) {
          if (c.appType) map[c.appType] = c.icon || null;
        }
        setAppIconByType(map);
      })
      .catch(() => { /* icons are best-effort; fall back to LetterAvatar */ });
    return () => { alive = false; };
  }, []);

  // Build the pinned nav entries (agents first, then apps — mirrors the
  // Agents-above-Apps order of the primary nav). Each carries a stable id,
  // label, icon URL, and detail-page target.
  // Account-wide: each row carries its OWN projectId, so paths + the per-project
  // pin check both key off the entry's project (not the current one). No
  // projectId guard — the list shows everywhere and survives project switches.
  const pinnedEntries = [
    ...agents
      .filter((p) => isPinnedFor(p.projectId, agentPinId(p)))
      .map((p) => ({
        id: `agent:${p.projectId}:${agentPinId(p)}`,
        label: p.displayName || p.workflowType,
        iconUrl: p.iconUrl,
        path: `/projects/${p.projectId}/agents/${p.workflowType}`,
      })),
    ...apps
      .filter((a) => isPinnedFor(a.projectId, appPinId(a)))
      .map((a) => ({
        id: `app:${a.projectId}:${appPinId(a)}`,
        label: a.name || a.appType || a.appSlug || a.instanceId,
        iconUrl: appIconByType[a.appType] || appIconByType[a.appSlug] || null,
        path: a.deployMode === 'solo'
          ? `/projects/${a.projectId}/apps/solo/${a.appSlug}`
          : `/projects/${a.projectId}/apps/${a.instanceId}`,
      })),
  ];

  // Check if we're on a project-scoped page.
  //
  // The OLD top-level execution detail route /projects/:projectId/runs/:id
  // was rendered OUTSIDE ProjectLayout, so we excluded it to fall back to
  // the global nav. The check used `/runs/` as a substring — fine for the
  // old route but it ALSO caught the newer pipeline-scoped run page
  // (/projects/:id/pipelines/:type/runs/:exec) which IS inside a project
  // and should keep the project nav. Tighten the match to the exact
  // legacy shape: `/projects/<id>/runs/<id>` with no segments between.
  const isLegacyTopLevelRun = /^\/projects\/[^/]+\/runs\//.test(location.pathname);
  const isProjectPage = projectId && !isLegacyTopLevelRun;

  // Project-specific menu (when viewing a project).
  // Tickets + Issues are hidden from the sidebar — Heroku-style focus on
  // Pipelines as the primary unit. Routes still exist (deep links work);
  // we just don't surface them in the nav. Bring back by re-adding entries
  // with `ticketOutline` / `fileTrayFullOutline` if needed.
  // Imports for those icons are kept so reverting is one-line.
  // playOutline kept for the (removed) Test Runs entry — re-add in one line.
  void ticketOutline; void fileTrayFullOutline; void playOutline; void arrowBackOutline; void gridOutline;
  const projectMenuItems = [
    {
      section: 'Project',
      // Pinned apps/agents render immediately after this section's items
      // (i.e. after Apps). See the render loop below.
      showPinned: true,
      items: [
        {
          path: `/projects/${projectId}/agents`,
          label: 'Agents',
          icon: gitNetworkOutline,
          description: 'Deployed agents'
        },
        {
          path: `/projects/${projectId}/apps`,
          label: 'Apps',
          icon: cubeOutline,
          description: 'Deployed managed app instances'
        }
        // Test Runs intentionally removed from the sidebar — execution history
        // is reached from each agent/app's detail page. Route still exists.
      ]
    },
    {
      section: 'Navigation',
      // Anchored to the foot of the sidebar (above the user-profile block),
      // so Settings + All Projects sit at the bottom regardless of how many
      // pinned items are in the top group.
      bottomPinned: true,
      items: [
        {
          path: '/projects',
          label: 'Projects',
          customIcon: FourDotsIcon,
          description: 'All your projects'
        },
        {
          path: `/projects/${projectId}/settings`,
          label: 'Settings',
          icon: settingsOutline,
          description: 'Project configuration'
        }
      ]
    },
  ];

  // Global menu (when not on a project page)
  const globalMenuItems = [
    {
      section: 'Main',
      items: [
        {
          path: '/projects',
          label: t('nav.projects'),
          customIcon: FourDotsIcon,
          description: 'Your projects'
        },
        {
          path: '/integrations',
          label: t('nav.integrations'),
          icon: extensionPuzzleOutline,
          description: 'Connect your services'
        }
      ]
    },
    {
      section: 'Account',
      items: [
        {
          path: '/billing',
          label: t('nav.billing'),
          icon: cardOutline,
          description: 'Manage your subscription'
        },
        {
          path: '/usage',
          label: t('nav.usage'),
          icon: trendingUpOutline,
          description: 'View your usage'
        },
      ]
    },
    {
      section: 'Support',
      items: [
        {
          path: '/settings/tokens',
          label: 'Tokens',
          icon: keyOutline,
          description: 'Personal access tokens'
        },
        {
          path: '/contact',
          label: t('nav.contact'),
          icon: mailOutline,
          description: 'Get help and support'
        },
      ]
    }
  ];

  // Choose menu based on context
  const menuItems = isProjectPage ? projectMenuItems : globalMenuItems;

  const isActivePath = (path) => {
    if (path === '/integrations') {
      // `/` shows the Projects page, NOT integrations — used to falsely
      // light up this item on the root route (visible after the Sentry
      // OAuth callback bounced through /). Keep `/calendar*` as a
      // legacy alias since the calendar route redirects to /integrations.
      return location.pathname === '/integrations' || location.pathname.startsWith('/calendar');
    }
    if (path === '/projects') {
      // Root `/` IS the Projects page (see App.js routesPath.root →
      // ProjectsPage). Both URLs render the same view, so both should
      // highlight the Projects item in the sidebar.
      return location.pathname === '/' || location.pathname === '/projects';
    }
    // For project-specific routes
    if (projectId) {
      // Agents list + any agent detail page (`/agents/<type>`) + any nested
      // route under an agent (e.g. `/agents/<type>/runs/<id>` when the inline
      // run detail is open). Without the trailing-anything match, the
      // sidebar's Agents item lost its active state as soon as the user
      // clicked into a specific run. `agents` is the canonical segment; the
      // legacy `pipelines` aliases still resolve, so match both.
      if (path === `/projects/${projectId}/agents`) {
        return location.pathname === `/projects/${projectId}/agents` ||
               location.pathname === `/projects/${projectId}/pipelines` ||
               location.pathname.match(/^\/projects\/[^/]+\/(agents|pipelines)(\/.+)?$/) ||
               // Agents marketplace lives under /marketplace — there's no
               // standalone "Marketplace" nav item, so the agents surface
               // lights up Agents (its deploy target). Bare /marketplace
               // defaults to the agents tab; /marketplace/apps is the one
               // exception, handled by the Apps item below.
               (location.pathname.match(/^\/projects\/[^/]+\/marketplace(\/.*)?$/) &&
                !location.pathname.match(/^\/projects\/[^/]+\/marketplace\/apps(\/.*)?$/));
      }
      if (path === `/projects/${projectId}/tickets`) {
        // Match tickets list, ticket details, implementation details, and analysis details pages
        return location.pathname === `/projects/${projectId}/tickets` || 
               location.pathname.match(/^\/projects\/[^/]+\/tickets\/[^/]+$/) ||
               location.pathname.match(/^\/projects\/[^/]+\/implementations\/[^/]+$/) ||
               location.pathname.match(/^\/projects\/[^/]+\/analysis\/[^/]+$/);
      }
      if (path === `/projects/${projectId}/issues`) {
        return location.pathname === `/projects/${projectId}/issues` ||
               location.pathname.match(/^\/projects\/[^/]+\/issues\/[^/]+$/);
      }
      if (path === `/projects/${projectId}/settings`) {
        return location.pathname === `/projects/${projectId}/settings`;
      }
      if (path === `/projects/${projectId}/marketplace`) {
        // Marketplace list + any future sub-routes (e.g. workflow detail
        // page outside the modal). Matches /marketplace and /marketplace/anything.
        return location.pathname === `/projects/${projectId}/marketplace` ||
               location.pathname.match(/^\/projects\/[^/]+\/marketplace(\/.+)?$/);
      }
      if (path === `/projects/${projectId}/apps`) {
        // Apps list + any app detail page (`/apps/<instanceId>`). Mirror of
        // the Pipelines matcher above so the sidebar item stays lit while
        // the user drills into one specific instance. Also lights up on the
        // apps marketplace surface (/marketplace/apps), the mirror of the
        // workflow-marketplace → Pipelines rule above.
        return location.pathname === `/projects/${projectId}/apps` ||
               location.pathname.match(/^\/projects\/[^/]+\/apps(\/.+)?$/) ||
               location.pathname.match(/^\/projects\/[^/]+\/marketplace\/apps(\/.*)?$/);
      }
      if (path === `/projects/${projectId}/testruns`) {
        // The fallback below catches "anything under /projects/<id>/ that
        // isn't an explicit other section" — when we added Pipelines as
        // its own nav item, /pipelines also fell through to here, lighting
        // both Pipelines AND Test Runs simultaneously. Add /pipelines to
        // the exclude list so only the right one highlights.
        return location.pathname === `/projects/${projectId}` ||
               location.pathname === `/projects/${projectId}/testruns` ||
               (location.pathname.startsWith(`/projects/${projectId}`) &&
                !location.pathname.includes('/tickets') &&
                !location.pathname.includes('/issues') &&
                !location.pathname.includes('/implementations') &&
                !location.pathname.includes('/analysis') &&
                !location.pathname.includes('/settings') &&
                !location.pathname.includes('/marketplace') &&
                !location.pathname.includes('/apps') &&
                !location.pathname.includes('/pipelines'));
      }
    }
    return location.pathname === path;
  };

  const handleNavClick = async () => {
    if (isMobile) {
      await menuController.close('sidebar-menu');
      if (onNavClick) onNavClick(); // Call parent callback to reset state
    }
  };

  return (
    <nav className={`sidebar-nav ${className} ${collapsed ? 'sidebar-nav--collapsed' : ''}`}>
      {menuItems.map((section, index) => (
        <div key={index} className={`nav-section ${section.bottomPinned ? 'nav-section--bottom' : ''}`}>
          <ul className="nav-list">
            {section.items.map((item, itemIndex) => (
              <li key={itemIndex} className="nav-item">
                <Link
                  to={item.path}
                  className={`nav-link ${isActivePath(item.path) ? 'active' : ''} ${item.isBackButton ? 'back-button' : ''}`}
                  onClick={handleNavClick}
                  title={collapsed ? item.label : undefined}
                >
                  <span className="nav-icon">
                    {item.customIcon || <IonIcon icon={item.icon} />}
                  </span>
                  {!collapsed && (
                    <div className="nav-content">
                      <span className="nav-label">{item.label}</span>
                    </div>
                  )}
                </Link>
              </li>
            ))}
            {section.showPinned && pinnedEntries.length > 0 && (
              <li className="nav-pinned-divider" aria-hidden="true" />
            )}
            {section.showPinned && pinnedEntries.map((entry) => (
              <PinnedNavLink
                key={entry.id}
                to={entry.path}
                iconUrl={entry.iconUrl}
                label={entry.label}
                active={location.pathname === entry.path}
                collapsed={collapsed}
                onClick={handleNavClick}
              />
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
};

export default NavigationMenu;
